import { type FunctionComponent, useEffect, useState } from "react";
import { graphql, useFragment } from "react-relay";
import {
  HomepageThemeFindOutMoreDisplay,
  HomepageThemeSignUpButtonDisplay,
  NewsletterVariant,
  useNewsletterApi,
  useSignedUpNewsletter,
} from "scmp-sdk";
import type { NewsletterItem } from "scmp-sdk/components/newsletter/types";

import type { NewsletterPackageDetail, ValidNewsletterPackageKey } from "shared/data/newsletter";
import { notEmpty } from "shared/lib/utils";

import { getNextNewsletterPackage } from "scmp-app/components/newsletter/helpers";
import { NewsletterWidgetContainer } from "scmp-app/components/newsletter/newsletter-widget-container";
import type { sectionNewsletterWidgetQuery$key } from "scmp-app/queries/__generated__/sectionNewsletterWidgetQuery.graphql";

import { WidgetPlacement } from "./consts";

export type Props = {
  className?: string;
  reference: sectionNewsletterWidgetQuery$key;
};

export const SectionNewsletterWidget: FunctionComponent<Props> = ({
  className,
  reference: reference_,
}) => {
  const { section } = useFragment(
    graphql`
      fragment sectionNewsletterWidgetQuery on Query
      @argumentDefinitions(entityId: { type: "String!" }) {
        section(filter: { entityId: $entityId }) {
          ... on Section {
            entityId
          }
        }
      }
    `,
    reference_,
  );

  const subscribedPackageIds = useSignedUpNewsletter();
  const { fetchNewsletterByIds, isNewslettersDataReady } = useNewsletterApi();
  const sectionEntityId = section?.entityId ?? "";

  const [packageKey, setPackageKey] = useState<ValidNewsletterPackageKey>();
  const [selectedPackage, setSelectedPackage] = useState<NewsletterPackageDetail>();
  const [childNewsletterItems, setChildNewsletterItems] = useState<NewsletterItem[]>();

  useEffect(() => {
    void (async () => {
      const placement = WidgetPlacement[sectionEntityId];
      if (placement) {
        const packageToShow = await getNextNewsletterPackage(placement, subscribedPackageIds);
        const packageDetail = packageToShow?.packageDetail;
        setSelectedPackage(packageDetail);
        setPackageKey(packageToShow?.packageKey);

        if (notEmpty(packageDetail?.childNewsletterIds)) {
          const data = await fetchNewsletterByIds(
            packageDetail?.childNewsletterIds,
            packageDetail?.childNewsletterIds.length,
          );
          setChildNewsletterItems(data?.filter(notEmpty) ?? []);
          return;
        }
        setChildNewsletterItems([]);
      }
    })();
  }, [fetchNewsletterByIds, sectionEntityId, subscribedPackageIds]);

  if (!isNewslettersDataReady && childNewsletterItems === undefined) return null;

  return (
    <NewsletterWidgetContainer
      className={className}
      findOutMoreButtonDisplayComponent={<HomepageThemeFindOutMoreDisplay />}
      multipleNewsletters
      newsletterItems={childNewsletterItems ?? []}
      packageDisplayName={selectedPackage?.packageDisplayName}
      packageKey={packageKey}
      signUpButtonDisplayComponent={<HomepageThemeSignUpButtonDisplay />}
      useOnlyPackageNewsletters
      variant={NewsletterVariant.Section}
    />
  );
};

SectionNewsletterWidget.displayName = "SectionNewsletterWidget";
